import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import PageShell from "../components/PageShell";
import { caseStudies } from "../data/caseStudies";
import { ArrowRight, ArrowLeft, Quote, PlayCircle, ExternalLink, CheckCircle2 } from "lucide-react";

export default function CaseStudy() {
  const { slug } = useParams();
  const cs = caseStudies[slug];
  if (!cs) return <Navigate to="/#portfolio" replace />;

  return (
    <PageShell kicker={`Case study · ${cs.client}`} title={cs.title} subtitle={cs.tag}>
      {/* Back link */}
      <div className="max-w-5xl mx-auto px-5 lg:px-8 pt-4">
        <Link to="/#portfolio" className="inline-flex items-center gap-2 text-xs tracking-widest uppercase font-semibold text-[color:var(--dms-navy)]/70 hover:text-[color:var(--dms-navy)]" data-testid="cs-back">
          <ArrowLeft className="w-4 h-4" /> Back to portfolio
        </Link>
      </div>

      {/* Hero visual */}
      <section className="max-w-6xl mx-auto px-5 lg:px-8 pt-6 pb-12">
        {cs.videoUrl ? (
          cs.videoUrl.toLowerCase().endsWith(".mp4") ? (
            <div className="relative rounded-3xl overflow-hidden bg-[color:var(--dms-navy)] aspect-video shadow-[0_30px_80px_-20px_rgba(20,36,61,0.45)]" data-testid="cs-video">
              <video
                src={cs.videoUrl}
                poster={cs.hero}
                controls
                playsInline
                preload="metadata"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          ) : (
            <div className="relative rounded-3xl overflow-hidden bg-[color:var(--dms-navy)] aspect-video shadow-[0_30px_80px_-20px_rgba(20,36,61,0.45)]" data-testid="cs-video">
              <iframe
                src={cs.videoUrl}
                title={cs.title}
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )
        ) : (
          <div className="relative rounded-3xl overflow-hidden bg-[color:var(--dms-navy)]" data-testid="cs-hero-img">
            <img src={cs.hero} alt={cs.title} className="w-full h-[380px] md:h-[520px] object-cover opacity-90" />
            <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--dms-navy)]/70 via-transparent to-transparent" />
            {cs.kind === "video" && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 rounded-full bg-[color:var(--dms-peach)] flex items-center justify-center shadow-2xl">
                  <PlayCircle className="w-10 h-10 text-[color:var(--dms-navy)]" />
                </div>
              </div>
            )}
            {cs.url && (
              <div className="absolute left-6 bottom-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/90 backdrop-blur text-sm font-mono italic text-[color:var(--dms-navy)]">
                <ExternalLink className="w-3.5 h-3.5" /> {cs.url}
              </div>
            )}
          </div>
        )}
      </section>

      {/* KPIs */}
      <section className="max-w-5xl mx-auto px-5 lg:px-8 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {cs.kpis.map((k, i) => (
            <div key={i} className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-7 text-center" data-testid={`cs-kpi-${i}`}>
              <p className="font-serif text-4xl md:text-5xl text-[color:var(--dms-navy)]">{k.value}</p>
              <p className="mt-2 text-[11px] tracking-widest uppercase text-[color:var(--dms-peach)] font-semibold">{k.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Before / After */}
      <section className="bg-[color:var(--dms-cream-2)]/50 py-16">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="text-center">
            <p className="kicker">The work</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[color:var(--dms-navy)] mt-4">Before &amp; after</h2>
            <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-7" data-testid="cs-before">
              <p className="text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/50 font-semibold">Before</p>
              <p className="mt-3 text-[color:var(--dms-navy)]/80 leading-relaxed">{cs.before}</p>
            </div>
            <div className="bg-[color:var(--dms-navy)] rounded-2xl p-7 text-white" data-testid="cs-after">
              <p className="text-[11px] tracking-widest uppercase text-[color:var(--dms-peach)] font-semibold flex items-center gap-2"><CheckCircle2 className="w-3 h-3" /> After</p>
              <p className="mt-3 text-white/90 leading-relaxed">{cs.after}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      {cs.gallery?.length > 0 && (
        <section className="max-w-6xl mx-auto px-5 lg:px-8 py-16">
          <div className="text-center">
            <p className="kicker">Shots</p>
            <h2 className="font-serif text-3xl text-[color:var(--dms-navy)] mt-4">Along the way</h2>
            <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {cs.gallery.map((g, i) => (
              <div key={i} className="rounded-2xl overflow-hidden aspect-[4/5] bg-[color:var(--dms-cream-2)]" data-testid={`cs-gallery-${i}`}>
                <img src={g} alt={`Shot ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition duration-500" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Client quote */}
      {cs.quote && (
        <section className="bg-[color:var(--dms-navy)] py-20">
          <div className="max-w-3xl mx-auto px-5 text-center" data-testid="cs-quote">
            <Quote className="w-9 h-9 text-[color:var(--dms-peach)] mx-auto" />
            <p className="font-serif italic text-2xl md:text-3xl text-white mt-6 leading-snug">"{cs.quote.text}"</p>
            <div className="w-10 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-8" />
            <p className="mt-5 text-white/80">{cs.quote.author}</p>
            <p className="mt-1 text-xs tracking-widest uppercase text-[color:var(--dms-peach)]">{cs.quote.role}</p>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="max-w-4xl mx-auto px-5 lg:px-8 py-20 text-center">
        <p className="kicker">Your story next</p>
        <h2 className="font-serif text-3xl md:text-4xl text-[color:var(--dms-navy)] mt-4">Want results like this for your business?</h2>
        <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <Link to="/#intake" className="btn-peach" data-testid="cs-start">Start your project <ArrowRight className="w-4 h-4" /></Link>
          <Link to="/#packages" className="btn-ghost-navy" data-testid="cs-packages">See packages <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>
    </PageShell>
  );
}
